import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import PT from 'prop-types';
import styled, { css } from 'styled-components';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-701df069.js';
import { g as getTranslationComponent } from '../i18n/AbandonmentDialogTranslation/index-fe39dd40.js';
import { M as Modal } from '../Modal/Modal-f212fbfb.js';
import { S as SecondaryView } from '../SecondaryView/SecondaryView-32dafb27.js';
import { u as useIsMobile } from '../theme/MobileThemeProvider/useIsMobile-6efebbf8.js';
import { T as Text } from '../Text/Text-f48a0c0d.js';
import { C as CancelButton } from './DefaultAbandonmentFooter/CancelButton-cc6deb1f.js';
import { L as LeaveButton } from './DefaultAbandonmentFooter/LeaveButton-39cf8eb4.js';
import { I as IntlProvider } from '../LocalModules/storm-ui-intl/dist/esm/IntlProvider-32f3f4a3-747df403.js';
import { u as useIntl } from '../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-8b5154d9-98867d9c.js';
import { D as DefaultAbandonmentFooter } from './DefaultAbandonmentFooter/DefaultAbandonmentFooter-d8ba10c2.js';

const MobileHeaderContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30051113__sc-j8k5ou-0"
})(["padding-inline-start:", ";padding-inline-end:", ";"], ({
  theme
}) => theme.spacing.base, ({
  theme
}) => theme.spacing.base);
const MobileFooterContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30051113__sc-j8k5ou-1"
})(["padding:", ";display:flex;justify-content:flex-end;"], ({
  theme
}) => theme.spacing.base);
const ChildrenWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30051113__sc-j8k5ou-2"
})(["padding-block-start:", ";padding-block-end:", ";padding-inline-start:", ";padding-inline-end:", ";", ""], ({
  $paddingBlockStart,
  theme
}) => $paddingBlockStart ? theme.spacing[$paddingBlockStart] : theme.spacing.large, ({
  $paddingBlockEnd,
  theme
}) => $paddingBlockEnd ? theme.spacing[$paddingBlockEnd] : theme.spacing.large, ({
  $paddingInlineStart,
  theme
}) => $paddingInlineStart ? theme.spacing[$paddingInlineStart] : theme.spacing.base, ({
  $paddingInlineEnd,
  theme
}) => $paddingInlineEnd ? theme.spacing[$paddingInlineEnd] : theme.spacing.base, isMobile(css(["padding-block-start:", ";padding-block-end:", ";padding-inline-start:", ";padding-inline-end:", ";"], ({
  $paddingBlockStart,
  theme
}) => $paddingBlockStart ? theme.mobile.spacing[$paddingBlockStart] : theme.mobile.spacing.base, ({
  $paddingBlockEnd,
  theme
}) => $paddingBlockEnd ? theme.mobile.spacing[$paddingBlockEnd] : theme.mobile.spacing.base, ({
  $paddingInlineStart,
  theme
}) => $paddingInlineStart ? theme.mobile.spacing[$paddingInlineStart] : theme.mobile.spacing.base, ({
  $paddingInlineEnd,
  theme
}) => $paddingInlineEnd ? theme.mobile.spacing[$paddingInlineEnd] : theme.mobile.spacing.base)));
const AbandonmentDialogContent = ({
  isOpen,
  onCancel,
  onLeave,
  paddingBlockStart,
  paddingBlockEnd,
  paddingInlineStart,
  paddingInlineEnd,
  renderHeader,
  renderFooter,
  taktId,
  children,
  ...rest
}) => {
  const isMobile = useIsMobile();
  const {
    formatMessage
  } = useIntl();
  const getDefaultHeader = () => isMobile ? /*#__PURE__*/React__default.createElement(MobileHeaderContainer, null, /*#__PURE__*/React__default.createElement(Text, {
    type: "h1",
    styleAs: "h4",
    withMargin: true
  }, formatMessage({
    id: 'default_abandonment_header'
  }))) : formatMessage({
    id: 'default_abandonment_header'
  });
  const header = renderHeader ? renderHeader() : getDefaultHeader();
  const footer = renderFooter ? renderFooter({
    handleCancel: onCancel,
    handleLeave: onLeave
  }) : /*#__PURE__*/React__default.createElement(DefaultAbandonmentFooter, _extends({}, rest, {
    onCancel: onCancel,
    onLeave: onLeave
  }));
  return isMobile ? /*#__PURE__*/React__default.createElement(SecondaryView, _extends({}, rest, {
    padding: "none",
    isOpen: isOpen,
    onClose: onCancel,
    closeButtonLabel: formatMessage({
      id: 'cancel'
    }),
    taktId: taktId
  }), header, /*#__PURE__*/React__default.createElement(ChildrenWrapper, {
    $paddingBlockStart: paddingBlockStart,
    $paddingBlockEnd: paddingBlockEnd,
    $paddingInlineStart: paddingInlineStart,
    $paddingInlineEnd: paddingInlineEnd
  }, children !== null && children !== void 0 ? children : /*#__PURE__*/React__default.createElement(Text, null, formatMessage({
    id: 'default_abandonment_body'
  }))), /*#__PURE__*/React__default.createElement(MobileFooterContainer, null, footer)) : /*#__PURE__*/React__default.createElement(Modal, _extends({}, rest, {
    padding: "none",
    isOpen: isOpen,
    header: header,
    footer: footer,
    onClose: onCancel,
    taktId: taktId
  }), /*#__PURE__*/React__default.createElement(ChildrenWrapper, {
    $paddingBlockStart: paddingBlockStart,
    $paddingBlockEnd: paddingBlockEnd,
    $paddingInlineStart: paddingInlineStart,
    $paddingInlineEnd: paddingInlineEnd
  }, children !== null && children !== void 0 ? children : /*#__PURE__*/React__default.createElement(Text, null, formatMessage({
    id: 'default_abandonment_body'
  }))));
};
AbandonmentDialogContent.defaultProps = {
  paddingBlockStart: undefined,
  paddingBlockEnd: undefined,
  paddingInlineStart: undefined,
  paddingInlineEnd: undefined,
  renderHeader: undefined,
  renderFooter: undefined
};

// eslint-disable-next-line max-len

const AbandonmentDialog = ({
  locale = 'en-US',
  onError,
  fallback,
  onReset,
  ...rest
}) => {
  const translations = getTranslationComponent(locale);
  return /*#__PURE__*/React__default.createElement(IntlProvider, {
    onError: onError,
    resetKey: locale,
    fallback: fallback,
    onReset: onReset,
    translations: translations
  }, /*#__PURE__*/React__default.createElement(AbandonmentDialogContent, rest));
};
AbandonmentDialog.propTypes = {
  // Indicates if the dialog should be displayed
  isOpen: PT.bool.isRequired,
  // Function to handle 'Continue Working' action
  onCancel: PT.func.isRequired,
  // Function to handle 'Leave Page' action
  onLeave: PT.func.isRequired,
  // Sets the padding around the modal's content.
  paddingBlockStart: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  paddingBlockEnd: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  paddingInlineStart: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  paddingInlineEnd: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  // Render prop for custom header content
  renderHeader: PT.func,
  // Render prop for custom footer content, with callbacks for Cancel and Leave actions
  renderFooter: PT.func,
  // the locale used for any internal translate strings, defaults to 'en-US'
  locale: PT.string
};
AbandonmentDialog.defaultProps = {
  paddingBlockStart: undefined,
  paddingBlockEnd: undefined,
  paddingInlineStart: undefined,
  paddingInlineEnd: undefined,
  renderHeader: undefined,
  renderFooter: undefined,
  locale: 'en-US'
};
AbandonmentDialog.Cancel = CancelButton;
AbandonmentDialog.Leave = LeaveButton;
var AbandonmentDialog$1 = AbandonmentDialog;

export { AbandonmentDialog$1 as A };
//# sourceMappingURL=AbandonmentDialog-d3e0dcc9.js.map
